import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { MapPin, Clock, Package, Loader2, ExternalLink, Truck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const OVERALL_COLORS = {
  'Livré': 'bg-emerald-600 text-white',
  'Arrivé destination': 'bg-blue-600 text-white',
  'En transit': 'bg-amber-500 text-white',
  'Pris en charge': 'bg-slate-500 text-white',
};

const LAPOSTE_STEP_LABELS = {
  1: 'Prise en charge',
  2: 'Acheminement',
  3: 'Arrivée sur site',
  4: 'En cours de livraison',
  5: 'Livré',
};

export function TrackingDialog({ open, onOpenChange, trackingNumber }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTracking = async (num) => {
    if (!num) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/tracking/${num}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Erreur de suivi';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (open && trackingNumber) {
      setData(null);
      fetchTracking(trackingNumber);
    }
  }, [open, trackingNumber]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="tracking-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package size={18} />
            Suivi colis — {trackingNumber}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-slate-400" />
            <span className="ml-2 text-sm text-slate-500">Chargement du suivi...</span>
          </div>
        )}

        {error && !loading && (
          <div className="text-center py-8">
            <p className="text-sm text-red-600">{error}</p>
            <Button size="sm" variant="outline" className="mt-3" onClick={() => fetchTracking(trackingNumber)}>Réessayer</Button>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <Badge data-testid="tracking-overall-status" className={`text-sm px-3 py-1 ${OVERALL_COLORS[data.overall_status] || 'bg-slate-500 text-white'}`}>
                {data.overall_status}
              </Badge>
              <span className="text-xs text-slate-500">{data.events?.length || 0} événements</span>
              <Button size="sm" variant="outline" className="h-6 text-xs gap-1 ml-auto" onClick={() => {
                const url = `${window.location.origin}/track?n=${trackingNumber}`;
                navigator.clipboard.writeText(url);
                toast.success('Lien copié !');
              }}>
                <ExternalLink size={11} /> Copier lien client
              </Button>
            </div>

            {data.delivered_date && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <p className="text-sm font-semibold text-emerald-800">
                  Livré le {new Date(data.delivered_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                </p>
              </div>
            )}

            {/* Estimated delivery */}
            {data.events?.length > 0 && !data.delivered_date && (() => {
              const firstEvent = data.events[data.events.length - 1];
              const estimated = new Date(new Date(firstEvent.date).getTime() + 15 * 24 * 60 * 60 * 1000);
              return (
                <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <p className="text-sm font-bold text-slate-800">
                    Estimation de livraison le : {estimated.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                  </p>
                </div>
              );
            })()}

            {/* Progress Bar */}
            {(() => {
              const STEPS = [
                { key: 'pris', label: 'Pris en charge' },
                { key: 'transit', label: 'En transit' },
                { key: 'destination', label: 'Arrivé destination' },
                { key: 'livre', label: 'Livré' },
              ];
              const statusMap = { 'Pris en charge': 0, 'En transit': 1, 'Arrivé destination': 2, 'Livré': 3 };
              const idx = statusMap[data.overall_status] ?? 0;
              return (
                <div className="py-1">
                  <div className="flex items-center justify-between relative">
                    <div className="absolute top-3.5 left-5 right-5 h-0.5 bg-slate-200 rounded-full" />
                    <div className="absolute top-3.5 left-5 h-0.5 bg-emerald-500 rounded-full transition-all duration-700" style={{ width: `calc(${(idx / (STEPS.length - 1)) * 100}% - 40px)` }} />
                    {STEPS.map((step, i) => (
                      <div key={step.key} className="flex flex-col items-center relative z-10" style={{ flex: 1 }}>
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold border-2 transition-all ${
                          i <= idx ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white border-slate-300 text-slate-400'
                        }`}>{i < idx ? '✓' : i + 1}</div>
                        <span className={`text-[10px] mt-1 text-center font-medium ${i <= idx ? 'text-emerald-700' : 'text-slate-400'}`}>{step.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* La Poste Colissimo Section */}
            {data.laposte?.available && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg overflow-hidden" data-testid="laposte-tracking-section">
                <div className="px-4 py-2.5 bg-amber-100 border-b border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Truck size={16} className="text-amber-700" />
                    <span className="text-sm font-bold text-amber-900">Suivi La Poste — Colissimo</span>
                  </div>
                  <a href={data.laposte.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-700 hover:underline flex items-center gap-1">
                    Voir sur laposte.fr <ExternalLink size={10} />
                  </a>
                </div>
                <div className="px-4 py-3 space-y-3">
                  {/* Estimated delivery date */}
                  {data.laposte.estim_date && !data.laposte.is_final && (
                    <div className="bg-white border border-amber-200 rounded-lg px-3 py-2.5 text-center">
                      <p className="text-xs text-slate-500 mb-0.5">La livraison de votre Colissimo est prévue</p>
                      <p className="text-base font-bold text-slate-900">
                        {new Date(data.laposte.estim_date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    </div>
                  )}

                  {/* La Poste Timeline Steps */}
                  {data.laposte.timeline?.length > 0 && (
                    <div className="flex items-center justify-between gap-1">
                      {data.laposte.timeline.map((step, i) => (
                        <div key={step.id} className="flex flex-col items-center flex-1">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold border-2 ${
                            step.done ? 'bg-amber-500 border-amber-500 text-white' : 'bg-white border-slate-300 text-slate-400'
                          }`}>
                            {step.done ? '✓' : step.id}
                          </div>
                          <span className={`text-[9px] mt-1 text-center leading-tight ${step.done ? 'text-amber-700 font-medium' : 'text-slate-400'}`}>
                            {LAPOSTE_STEP_LABELS[step.id] || step.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* La Poste Events */}
                  {data.laposte.events?.length > 0 && (
                    <div className="space-y-1.5">
                      {data.laposte.events.map((evt, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${i === 0 ? 'bg-amber-500' : 'bg-slate-300'}`} />
                          <div className="flex-1">
                            <p className={`${i === 0 ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>{evt.label}</p>
                            {evt.date && (
                              <p className="text-slate-400 text-[10px]">
                                {new Date(evt.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                                {' '}
                                {new Date(evt.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="relative">
              {data.events?.map((event, i) => (
                <div key={`${event.date}-${i}`} className="flex gap-3 pb-4 last:pb-0">
                  {/* Timeline dot and line */}
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full mt-1 flex-shrink-0 ${i === 0 ? 'bg-emerald-500 ring-2 ring-emerald-200' : 'bg-slate-300'}`} />
                    {i < data.events.length - 1 && <div className="w-0.5 flex-1 bg-slate-200 mt-1" />}
                  </div>
                  {/* Event content */}
                  <div className="flex-1 min-w-0 pb-1">
                    <p className={`text-sm font-semibold ${i === 0 ? 'text-slate-900' : 'text-slate-700'}`}>
                      {event.status}
                    </p>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {event.location && (
                        <span className="text-xs text-slate-500 flex items-center gap-1">
                          <MapPin size={11} /> {event.location}
                          {event.sub_status && <span className="text-slate-400">({event.sub_status})</span>}
                        </span>
                      )}
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock size={11} />
                        {new Date(event.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        {' '}
                        {new Date(event.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
              {(!data.events || data.events.length === 0) && (
                <p className="text-sm text-slate-500 text-center py-4">Aucune information de suivi disponible</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
